import React from 'react';
import { ArrowRight, CheckCircle2, Star } from 'lucide-react';
import { Button } from './ui/button';
import { clinicInfo } from '../data/mock';

const Hero = () => {
  const benefits = [
    'Free Initial Consultation',
    'Flexible Payment Plans',
    '15+ Years Experience',
  ];

  return (
    <section id="home" className="relative overflow-hidden gradient-hero">
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-teal-200/30 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-sky-200/30 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in-up">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-teal-100 rounded-full px-4 py-2 shadow-sm">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-sm font-medium text-slate-600">5.0 Rated on Google</span>
            </div>

            {/* Heading */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-800 leading-tight">
                {clinicInfo.tagline}
                <span className="block text-gradient mt-2">Starts Here</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl">
                Transform your smile with our expert orthodontic care. We combine 
                cutting-edge technology with personalized treatment plans for 
                results that last a lifetime.
              </p>
            </div>

            {/* Benefits */}
            <div className="flex flex-wrap gap-4">
              {benefits.map((benefit) => (
                <div
                  key={benefit}
                  className="flex items-center gap-2 text-slate-700"
                >
                  <CheckCircle2 className="w-5 h-5 text-teal-600" />
                  <span className="font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-6 text-lg btn-transition shadow-xl shadow-teal-600/25"
              >
                <a href="#appointment" className="flex items-center gap-2">
                  Schedule Free Consultation
                  <ArrowRight className="w-5 h-5" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-slate-300 hover:border-teal-600 hover:text-teal-600 font-semibold px-8 py-6 text-lg btn-transition"
              >
                <a href="#services">View Our Services</a>
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center gap-6 pt-4">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br from-teal-400 to-sky-400 flex items-center justify-center text-white text-xs font-bold shadow-md"
                  >
                    {['S', 'M', 'E', 'J'][i - 1]}
                  </div>
                ))}
              </div>
              <div>
                <p className="font-semibold text-slate-800">2,500+ Happy Patients</p>
                <p className="text-sm text-slate-500">Join our smile family today</p>
              </div>
            </div>
          </div>

          {/* Right Content - Image */}
          <div className="relative animate-fade-in delay-200">
            <div className="relative z-10">
              <img
                src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=600&h=700&fit=crop"
                alt="Smiling patient at orthodontist"
                className="rounded-3xl shadow-2xl w-full object-cover"
              />
              
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-slate-100 animate-pulse-gentle">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center">
                    <span className="text-2xl font-bold text-teal-600">15+</span>
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">Years of</p>
                    <p className="text-slate-500">Excellence</p>
                  </div>
                </div>
              </div>

              {/* Another floating element */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl border border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className="font-bold text-slate-800">4.9/5</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">500+ Reviews</p>
              </div>
            </div>

            {/* Background decoration */}
            <div className="absolute inset-0 -z-10 translate-x-4 translate-y-4">
              <div className="w-full h-full bg-teal-100 rounded-3xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
